import { useState, useEffect, useRef, useMemo, useContext, useCallback } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import List from 'components/Common/List'
import TextButton from 'LuxKit/Button/TextButton'
import ExperienceCancellationText from 'components/Experiences/ExperienceCancellationPolicy/ExperienceCancellationText'
import ExperienceGiveAsGiftInfoText from 'components/Experiences/ExperienceGiveAsGiftModal/ExperienceGiveAsGiftInfoText'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import MarkdownRender from 'LuxKit/MarkdownRender'
import ExperienceBookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/Experience/ExperienceBookmarkButton'
import Breakpoint from 'components/Common/Breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW } from 'constants/experience'
import ExperienceImageCarousel from './ExperienceImageCarousel'
import OffsetBookmarkContainer from 'tripPlanner/components/Bookmark/Common/OffsetBookmarkContainer'
import config from 'constants/config'
import { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import Clickable from 'components/Common/Clickable'
import { getImmersiveTripId } from 'tripPlanner/selectors'
import { useIntl } from 'react-intl'

import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { pushWithRegion } from 'actions/NavigationActions'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import clsx from 'clsx'
import Group from 'components/utils/Group'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import { useOfferInViewTracking } from 'hooks/useOfferInViewTracking'
import { getAgentHubExperienceType } from 'lib/agentHub/agentHubUtils'
import ExperienceAppDiscountTag from '../ExperienceAppDiscountTag'
import AgentHubAdditionalCommissionLabel from 'agentHub/components/AgentHubAdditionalCommissionLabel'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import { generateLoyaltyPointsCalculatorExperienceOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import getIsExperienceLoyaltyBurnEnabled from 'loyalty/selectors/getIsExperienceLoyaltyBurnEnabled'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import useExperienceSearchTileBurn from 'components/Experiences/hooks/useExperienceSearchTileBurn'
import ExperienceTilePricesWithBurn from './ExperienceTilePricesWithBurn'
import NumberRating from 'components/Common/NumberRating'
import { isExperienceOfferRatingDisplayable } from 'lib/order/reviewUtils'
import { formatDecimal } from 'lib/format/formatNumber'

const MapViewCTAs = styled.div`
  display: flex;
  flex-direction: column;
  opacity: 0;
  transition: opacity 0.2s ease;
  margin-top: ${rem(16)};
`

const Experience = styled(Clickable)`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: ${rem(220)} 1fr;
  border-radius: ${props => props.theme.borderRadius.M};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  overflow: hidden;
  position: relative;

  ${mediaQueryUp.tablet} {
    height: ${rem(284)};
    grid-template-columns: ${rem(260)} 1fr;
    grid-template-rows: 1fr;
  }

  &.map-view {
    border: none;

    &:hover ${MapViewCTAs} {
      opacity: 1;
    }
  }

  @media (min-width: 1710px) {
    &.map-view {
      height: ${rem(400)};
      grid-template-columns: ${rem(400)} 1fr;
      grid-template-rows: 1fr;
    }
  }
`

const ExperienceWrapper = styled.div`
  width: 100%;
`

const Description = styled.div`
  position: relative;
  padding: ${rem(20)};
  display: grid;
  gap: ${rem(12)};
  grid-template-columns: 1fr;

  ${mediaQueryUp.tablet} {
    padding: ${rem(20)} ${rem(24)};
    gap: ${rem(24)};
    grid-template-columns: 1fr ${rem(172)};
    overflow: hidden;
  }
`

const PriceSection = styled.div`
  display: flex;
  justify-content: space-between;

  ${mediaQueryUp.tablet} {
    flex-direction: column;
    justify-content: unset;

    > * + * {
      margin-top: ${rem(16)};
    }
  }

  ${mediaQueryDown.mobile} {
    flex-direction: column;
    justify-content: unset;
  }
`

const CTAContainer = styled.div`
  ${mediaQueryDown.mobile} {
    display: flex;
    flex-wrap: wrap;
    gap: ${rem(8)};
    margin-top: ${rem(16)};

    > * {
      flex: 1;
      min-width: ${rem(120)};
    }
  }

  ${mediaQueryUp.tablet} {
    margin-top: ${rem(16)};
  }
`

const DescriptionSection = styled(VerticalSpacer)`
  height: 100%;
  overflow: hidden;
  position: relative;
`

const BookmarkButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`

const MAX_CONTENT_HEIGHT = 115

const ContentBox = styled.div`
  &.max-content {
    overflow-y: hidden;
    max-height: ${rem(MAX_CONTENT_HEIGHT)};
  }

  &.map-view {
    overflow-y: hidden;
    max-height: ${rem(190)};
  }

  &.plus-more {
    left: 0;
    width: 100%;
    display: block;
    bottom: ${rem(-4)};
    position: absolute;
    padding: ${rem(24)} 0 0;
    background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 1) 50%
    );

    ${mediaHoverable} {
      > span:hover {
        opacity: 0.8;
      }
    }
  }
`
const OfferButton = styled(TextButton)`
`

const GiftButton = styled(TextButton)`
  ${mediaQueryUp.tablet} {
    margin-bottom: ${rem(8)};
  }

  ${mediaQueryDown.mobile} {
    margin-bottom: ${rem(16)};
  }
`

interface Props {
  experience: App.ExperienceOffer
  position?: number
  isMapView?: boolean
}

function ExperienceSearchTile(props: Props) {
  const { experience, position, isMapView = false } = props
  const intl = useIntl()
  const contentEl = useRef<HTMLDivElement>(null)
  const hasTrackedImpressionRef = useRef(false)
  const dispatch = useAppDispatch()
  const [showPlusMore, setShowPlusMore] = useState<boolean>(false)
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const trackEvent = useTrackExperienceOfferEvent()

  const { setTileRef, trackingInView } = useOfferInViewTracking()

  useEffect(() => {
    if (!trackingInView || position === undefined || hasTrackedImpressionRef.current) return

    hasTrackedImpressionRef.current = true

    dispatchOfferListEvent({
      type: OfferListEvents.productImpression,
      offer: experience,
      position,
      key: EventDataKey.ProductImpression,
    })

    trackEvent('OFFER_TILE_IMPRESSION', [{
      type: 'impression',
      data: {
        offer: experience,
      },
    }])
  }, [dispatchOfferListEvent, experience, position, trackEvent, trackingInView])

  useEffect(() => {
    const isBiggerThanHeight = (contentEl.current?.clientHeight ?? 0) > MAX_CONTENT_HEIGHT
    setShowPlusMore(isBiggerThanHeight)
  }, [])

  const tripId = useAppSelector(getImmersiveTripId)
  const isLoyaltyEnabled = useAppSelector(getIsExperienceLoyaltyBurnEnabled)
  const isLuxPlusEnabled = useAppSelector(getIsEnabledLuxPlusDiscountedExperiences)
  const isLuxPricingShown = isLuxPlusEnabled && !!experience.pricing.memberPrice
  const showCancellationPolicy = !isLuxPricingShown && !!experience.copy.cancellationInfo?.refundPolicies?.length

  const offerPageQueryParams = useMemo(() => {
    if (config.OPEN_NEW_TAB_OFFER_CLICK && tripId) return new URLSearchParams({ tripId })
    return undefined
  }, [tripId])

  const handleGiveAsGift = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault()
    trackEvent('GIVE_AS_GIFT_CLICKED', [{
      type: 'click',
      data: {
        offer: experience,
      },
    }])
    dispatch(pushWithRegion(`${getExperienceOfferPageURL(experience, offerPageQueryParams)}/?modal=gifting`))
  }

  const handleViewOffer = useCallback(() => {
    if (position !== undefined) {
      dispatchOfferListEvent({
        type: OfferListEvents.productClick,
        offer: experience,
        position,
        key: EventDataKey.ProductClick,
      })
    }

    trackEvent('OFFER_TILE_CLICKED', [{
      type: 'click',
      data: {
        offer: experience,
      },
    }])
  }, [experience, dispatchOfferListEvent, position, trackEvent])

  const handleMouseEnter = useCallback(() => {
    const event = new CustomEvent('offerHover', {
      detail: {
        id: experience.id,
        type: 'experience',
        persist: true,
      },
    })
    const rootElement = document.querySelector('[data-experience-search-tiles-root]') || document
    rootElement.dispatchEvent(event)
  }, [experience.id])

  const handleMouseLeave = useCallback(() => {
    const event = new CustomEvent('offerHover', {
      detail: {
        id: '',
        type: '',
        persist: false,
      },
    })
    const rootElement = document.querySelector('[data-experience-search-tiles-root]') || document
    rootElement.dispatchEvent(event)
  }, [])

  const productType = useMemo(() => getAgentHubExperienceType(experience), [experience])

  const tileBurn = useExperienceSearchTileBurn({
    totalPrice: experience.price,
    totalMemberPrice: experience.pricing.memberPrice,
    isLuxPlusEnabled,
    isLoyaltyEnabled,
  })

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorExperienceOptions(experience, {
      price: experience.price,
      memberPrice: experience.pricing.memberPrice,
    }),
  ], [experience])

  const ratingSubtitle = useMemo(() => {
    const reviewsTotal = experience.rating?.reviewsTotal ?? 0
    const reviewsTotalFormatted = formatDecimal(reviewsTotal, {
      regionCode: 'US',
      notation: 'compact',
      compactDisplay: 'short',
      maximumFractionDigits: reviewsTotal > 1000 ? 0 : 1,
    })
    return intl.formatMessage(
      {
        id: '7mB5TwY0sdJTirw8L6zS',
        defaultMessage: '{formattedCount} reviews',
        description: 'Review count subtitle in experience search tile component',
      },
      { formattedCount: reviewsTotalFormatted },
    )
  }, [experience.rating?.reviewsTotal, intl])

  const locationLabel = useMemo(() => {
    const redemptionLocationsCount = experience.redemptionLocations?.length ?? 0
    if (redemptionLocationsCount > 1) {
      return intl.formatMessage({
        id: 'KI0Ne2ADIUI5KqTcHQwb',
        defaultMessage: 'Multiple Locations',
        description: 'Label for experiences available at multiple locations',
      })
    }

    const redemptionCity = redemptionLocationsCount === 1 ? experience.redemptionLocations?.[0]?.city : undefined
    return redemptionCity ?? experience.location.city ?? experience.location.name
  }, [experience.location.city, experience.location.name, experience.redemptionLocations, intl])

  const giftLabel = intl.formatMessage({
    id: 'fuDPtJTTxqTDOHhMha4m',
    defaultMessage: 'Give as gift',
    description: 'Button text to give experience as gift on the gift card purchase or redemption page',
  })
  const viewOfferLabel = intl.formatMessage({
    id: '6HfGgocJpAVe54tmMlF6',
    defaultMessage: 'View offer',
    description: 'Button text to view experience offer details on the experience page',
  })

  const ctaSection = isMapView ? (
    <MapViewCTAs>
      {!!experience.giftType && (
        <GiftButton kind="secondary" fit="flex" onClick={handleGiveAsGift}>
          {giftLabel}
        </GiftButton>
      )}
      <OfferButton kind="primary" fit="flex" nonInteractable>
        {viewOfferLabel}
      </OfferButton>
    </MapViewCTAs>
  ) : (
    <>
      <CTAContainer>
        {!!experience.giftType && (
          <>
            <GiftButton kind="secondary" fit="flex" onClick={handleGiveAsGift}>
              {giftLabel}
            </GiftButton>
            <OfferButton kind="primary" fit="flex" nonInteractable>
              {viewOfferLabel}
            </OfferButton>
          </>
        )}
      </CTAContainer>
      {!experience.giftType && (
        <OfferButton kind="primary" fit="flex" nonInteractable>
          {intl.formatMessage({
            id: 'vNF0coped0YyEXqBJoBv',
            defaultMessage: 'View offer',
            description: 'Button text to view experience offer details for the experience',
          })}
        </OfferButton>
      )}
    </>
  )

  return (
    <ExperienceWrapper ref={setTileRef}>
      <Experience
        to={getExperienceOfferPageURL(experience, offerPageQueryParams)}
        target={config.OPEN_NEW_TAB_OFFER_CLICK ? '_blank' : undefined}
        rel={config.OPEN_NEW_TAB_OFFER_CLICK ? 'noopener noreferrer' : undefined}
        onClick={handleViewOffer}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={clsx({ 'map-view': isMapView })}
      >
        <ExperienceImageCarousel experience={experience} partnershipBadge />
        <Description>
          <TripGuard>
            <Breakpoint max="mobile">
              <OffsetBookmarkContainer>
                <ExperienceBookmarkButton offer={experience} />
              </OffsetBookmarkContainer>
            </Breakpoint>
          </TripGuard>
          <DescriptionSection gap={8}>
            <Group direction="horizontal" gap={4} verticalAlign="center">
              <ExperienceAppDiscountTag experienceOffer={experience} />
              <AgentHubAdditionalCommissionLabel offerId={experience?.id} />
            </Group>
            <VerticalSpacer gap={4}>
              <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between" gap={4}>
                <CaptionTextBlock variant="large" weight="bold" startIcon={<LineMapMarkerIcon />}>
                  {locationLabel}
                </CaptionTextBlock>
                <CSSBreakpoint max="mobile">
                  {config.EXPERIENCES_REVIEWS_ENABLED && isExperienceOfferRatingDisplayable(experience) && <NumberRating
                    rating={experience.rating?.score}
                    total={experience.rating?.reviewsTotal}
                    variant="small"
                    numberPosition="end"
                    onlyNumber
                  />}
                </CSSBreakpoint>
              </Group>
              <Heading variant="heading6">{experience.name}</Heading>
              {!!experience.giftType && (
                <ExperienceGiveAsGiftInfoText experience={experience} />
              )}
              <LoyaltyPointsEarn
                calculationRequests={pointsEarnCalculationRequests}
                calculationType="estimate"
                hideStatusCredits
                pointsToApply={tileBurn.pointsToApplyForEarn}
                burnPriceInPoints={tileBurn.priceInPointsForEarn}
              />
              {showCancellationPolicy && (
                <ExperienceCancellationText
                  cancellationInfo={experience.copy.cancellationInfo}
                />
              )}
            </VerticalSpacer>
            <div ref={contentEl}>
              <ContentBox className={clsx({ 'max-content': showPlusMore, 'map-view': isMapView })}>
                {experience.productType !== 'channel_manager' && (
                  <MarkdownRender
                    type="compact"
                    fontSize="small"
                    content={
                      experience.copy.included || experience.copy.description
                    }
                  />
                )}
                {experience.productType === 'channel_manager' && (
                  <>
                    {experience.copy.highlights.length === 0 && (
                      <MarkdownRender
                        type="compact"
                        fontSize="small"
                        content={experience.copy.description}
                      />
                    )}
                    {experience.copy.highlights.length > 0 && (
                      <List>
                        {experience.copy.highlights.map((highlight) => (
                          <li key={highlight}>
                            <BodyText variant="small" as="span">
                              {highlight}
                            </BodyText>
                          </li>
                        ))}
                      </List>
                    )}
                  </>
                )}
              </ContentBox>
            </div>
            {showPlusMore && (
              <ContentBox className="plus-more">
                <BodyText
                  weight="bold"
                  colour="primary"
                  variant="small"
                  as="span"
                >
                  {intl.formatMessage({
                    id: 'zQlrRvz_mxGpMkoms8ry',
                    defaultMessage: 'Plus more...',
                    description: 'Text indicating more content is available in experience search tile',
                  })}
                </BodyText>
              </ContentBox>
            )}
          </DescriptionSection>
          <PriceSection>
            <TripGuard>
              <CSSBreakpoint min="tablet">
                <Group direction="horizontal" verticalAlign="center" horizontalAlign="end" gap={2}>
                  {config.EXPERIENCES_REVIEWS_ENABLED && isExperienceOfferRatingDisplayable(experience) && <NumberRating
                    rating={experience.rating?.score}
                    ratingSubtitle={ratingSubtitle}
                    hideSubtitle
                    variant="small"
                    numberPosition="end"
                  />}
                  <BookmarkButtonContainer>
                    <ExperienceBookmarkButton offer={experience} size="small" />
                  </BookmarkButtonContainer>
                </Group>
              </CSSBreakpoint>
            </TripGuard>
            <Group direction="vertical" verticalAlign="end" fullHeight>
              <div>
                <ExperienceTilePricesWithBurn
                  tileBurn={tileBurn}
                  totalPrice={experience.price}
                  totalMemberPrice={experience.pricing.memberPrice}
                  isFromPrice
                  ticketUnitLabel={experience.ticketUnitLabel ?? intl.formatMessage({
                    id: 'W4CkMvR2dmG_t9VAGJeN',
                    defaultMessage: 'total',
                    description: 'Default sale unit label for experience search tile pricing',
                  })}
                  size="L"
                  nonMemberPriceSize="L"
                />
                {experience.discount >= EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW && <PriceRowValueDiscountWithCaption
                  data-testid="value-box"
                  size="M"
                  originalValue={Math.ceil(experience.grossPrice)}
                  discountPercentage={experience.discount}
                />}
                <PriceRowAgentHubCommission
                  offerId={experience.id}
                  productType={productType}
                  offerLocations={[experience.location.name]}
                  offer={experience}
                />
              </div>
            </Group>
            {ctaSection}
          </PriceSection>
        </Description>
      </Experience>
    </ExperienceWrapper>
  )
}

export default ExperienceSearchTile
